import React, { useState, useEffect } from 'react';
import { getRides, getDrivers, getCustomers } from '../firebase/database';

const REPORT_TYPES = [
  { id: 'rides_summary',    icon: '🚗', label: 'Rides Summary',      desc: 'All rides with status, driver, customer details' },
  { id: 'driver_activity',  icon: '👤', label: 'Driver Activity',     desc: 'Rides per driver, completion rate, performance' },
  { id: 'customer_history', icon: '🧑‍💼', label: 'Customer History',   desc: 'Ride history per customer' },
  { id: 'daily_report',     icon: '📅', label: 'Daily Report',        desc: 'All rides for a specific day' },
  { id: 'pending_rides',    icon: '⏳', label: 'Pending Rides',       desc: 'All unassigned and pending rides' },
  { id: 'completed_rides',  icon: '✅', label: 'Completed Rides',     desc: 'All completed rides in date range' },
  { id: 'cancelled_rides',  icon: '❌', label: 'Cancelled Rides',     desc: 'All cancelled rides with reasons' },
  { id: 'driver_list',      icon: '🚘', label: 'Driver Directory',    desc: 'Full list of all drivers and their details' },
  { id: 'customer_list',    icon: '👥', label: 'Customer Directory',  desc: 'Full list of all registered customers' },
];

export default function ReportsPage() {
  const [rides, setRides]         = useState([]);
  const [drivers, setDrivers]     = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [generating, setGenerating] = useState('');
  const [activeReport, setActiveReport] = useState(null);

  // Filters
  const [selectedDriver, setSelectedDriver]   = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [dateRange, setDateRange] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');
  const [specificDate, setSpecificDate] = useState(() => {
    // Use local date (not UTC) to avoid timezone mismatch with Firestore timestamps
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm   = String(now.getMonth() + 1).padStart(2, '0');
    const dd   = String(now.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });

  useEffect(() => {
    Promise.all([getRides(), getDrivers(), getCustomers()]).then(([r, d, c]) => {
      setRides(r);
      setDrivers(d);
      setCustomers(c);
      setLoading(false);
    });
  }, []);

  const getDateBounds = () => {
    const now = new Date();
    if (dateRange === 'today') {
      return { start: new Date(now.setHours(0,0,0,0)), end: new Date(now.setHours(23,59,59,999)) };
    } else if (dateRange === 'week') {
      const start = new Date(); start.setDate(start.getDate() - 7);
      return { start, end: new Date() };
    } else if (dateRange === 'month') {
      return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59) };
    } else if (dateRange === 'year') {
      return { start: new Date(now.getFullYear(), 0, 1), end: new Date(now.getFullYear(), 11, 31, 23, 59, 59) };
    } else {
      return {
        start: startDate ? new Date(startDate) : new Date(0),
        end: endDate ? new Date(endDate + 'T23:59:59') : new Date()
      };
    }
  };

  const filterRidesByDate = (ridesArr) => {
    const { start, end } = getDateBounds();
    return ridesArr.filter(r => {
      const dt = r.pickupDateTime?.toDate ? r.pickupDateTime.toDate() : new Date(r.pickupDateTime);
      return dt >= start && dt <= end;
    });
  };

  const formatDate = (ts) => {
    const dt = ts?.toDate ? ts.toDate() : new Date(ts);
    return isNaN(dt) ? 'N/A' : dt.toLocaleDateString('en-US');
  };

  const formatTime = (ts) => {
    const dt = ts?.toDate ? ts.toDate() : new Date(ts);
    return isNaN(dt) ? 'N/A' : dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateTime = (ts) => `${formatDate(ts)} ${formatTime(ts)}`;

  // ── Report Data Generators ─────────────────────────────────
  const getReportData = (reportId) => {
    const filtered = filterRidesByDate(rides);

    switch(reportId) {
      case 'rides_summary':
        return {
          title: 'Rides Summary Report',
          headers: ['Date', 'Time', 'Customer', 'Phone', 'Pickup Address', 'Driver', 'Stops', 'Status'],
          rows: filtered.map(r => [
            formatDate(r.pickupDateTime),
            formatTime(r.pickupDateTime),
            r.customerName || '—',
            r.customerPhone || '—',
            r.pickupAddress || '—',
            r.driverName || 'Unassigned',
            r.stops?.length || 0,
            r.status?.replace('_', ' ') || '—',
          ]),
          summary: { 'Total Rides': filtered.length, 'Completed': filtered.filter(r => r.status === 'completed').length, 'Pending': filtered.filter(r => r.status === 'pending').length, 'Cancelled': filtered.filter(r => r.status === 'cancelled').length },
        };

      case 'driver_activity':
        return {
          title: 'Driver Activity Report',
          headers: ['Driver', 'Phone', 'Vehicle', 'Total Rides', 'Completed', 'Cancelled', 'Pending', 'Completion Rate'],
          rows: drivers.map(d => {
            const driverRides = filtered.filter(r => r.driverId === d.id);
            const completed   = driverRides.filter(r => r.status === 'completed').length;
            const cancelled   = driverRides.filter(r => r.status === 'cancelled').length;
            const pending     = driverRides.filter(r => ['pending','assigned','accepted','in_progress'].includes(r.status)).length;
            const rate = driverRides.length > 0 ? Math.round((completed / driverRides.length) * 100) + '%' : 'N/A';
            return [d.name, d.phone || '—', d.vehicleNumber || '—', driverRides.length, completed, cancelled, pending, rate];
          }),
          summary: { 'Total Drivers': drivers.length, 'Active Drivers': drivers.filter(d => d.isActive).length },
        };

      case 'customer_history': {
        const custRides = selectedCustomer === 'all'
          ? filtered
          : filtered.filter(r => r.customerId === selectedCustomer);
        const cust = customers.find(c => c.id === selectedCustomer);
        return {
          title: selectedCustomer === 'all' ? 'All Customer Ride History' : `${cust?.name} - Ride History`,
          headers: ['Date', 'Time', 'Customer', 'Pickup Address', 'Driver', 'Status'],
          rows: custRides.map(r => [
            formatDate(r.pickupDateTime),
            formatTime(r.pickupDateTime),
            r.customerName || '—',
            r.pickupAddress || '—',
            r.driverName || 'Unassigned',
            r.status?.replace('_', ' ') || '—',
          ]),
          summary: { 'Total Rides': custRides.length, 'Completed': custRides.filter(r => r.status === 'completed').length },
        };
      }

      case 'daily_report': {
        const dayRides = rides.filter(r => {
          let dt;
          if (r.pickupDateTime?.toDate) {
            dt = r.pickupDateTime.toDate();
          } else if (r.pickupDateTime) {
            dt = new Date(r.pickupDateTime);
          } else {
            return false;
          }
          if (isNaN(dt)) return false;
          // Use LOCAL date parts to avoid UTC timezone shift
          const yyyy = dt.getFullYear();
          const mm   = String(dt.getMonth() + 1).padStart(2, '0');
          const dd   = String(dt.getDate()).padStart(2, '0');
          return `${yyyy}-${mm}-${dd}` === specificDate;
        });
        return {
          title: `Daily Report — ${new Date(specificDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`,
          headers: ['Time', 'Customer', 'Phone', 'Pickup Address', 'Driver', 'Stops', 'Status'],
          rows: dayRides.map(r => [
            formatTime(r.pickupDateTime),
            r.customerName || '—',
            r.customerPhone || '—',
            r.pickupAddress || '—',
            r.driverName || 'Unassigned',
            r.stops?.length || 0,
            r.status?.replace('_', ' ') || '—',
          ]),
          summary: { 'Total Rides': dayRides.length, 'Completed': dayRides.filter(r => r.status === 'completed').length, 'Pending': dayRides.filter(r => r.status === 'pending').length },
        };
      }

      case 'pending_rides': {
        const pending = rides.filter(r => ['pending','assigned'].includes(r.status));
        return {
          title: 'Pending & Unassigned Rides',
          headers: ['Date', 'Time', 'Customer', 'Phone', 'Pickup Address', 'Status'],
          rows: pending.map(r => [
            formatDate(r.pickupDateTime),
            formatTime(r.pickupDateTime),
            r.customerName || '—',
            r.customerPhone || '—',
            r.pickupAddress || '—',
            r.status || '—',
          ]),
          summary: { 'Pending (Unassigned)': rides.filter(r => r.status === 'pending').length, 'Assigned (Not Accepted)': rides.filter(r => r.status === 'assigned').length },
        };
      }

      case 'completed_rides': {
        const completed = filtered.filter(r => r.status === 'completed');
        return {
          title: 'Completed Rides Report',
          headers: ['Date', 'Time', 'Customer', 'Pickup Address', 'Driver', 'Stops'],
          rows: completed.map(r => [
            formatDate(r.pickupDateTime),
            formatTime(r.pickupDateTime),
            r.customerName || '—',
            r.pickupAddress || '—',
            r.driverName || '—',
            r.stops?.length || 0,
          ]),
          summary: { 'Total Completed': completed.length },
        };
      }

      case 'cancelled_rides': {
        const cancelled = filtered.filter(r => r.status === 'cancelled');
        return {
          title: 'Cancelled Rides Report',
          headers: ['Date', 'Time', 'Customer', 'Phone', 'Pickup Address', 'Driver'],
          rows: cancelled.map(r => [
            formatDate(r.pickupDateTime),
            formatTime(r.pickupDateTime),
            r.customerName || '—',
            r.customerPhone || '—',
            r.pickupAddress || '—',
            r.driverName || 'Unassigned',
          ]),
          summary: { 'Total Cancelled': cancelled.length },
        };
      }

      case 'driver_list':
        return {
          title: 'Driver Directory',
          headers: ['Name', 'Email', 'Phone', 'Vehicle No', 'Vehicle Type', 'Status', 'Registered'],
          rows: drivers.map(d => [
            d.name || '—',
            d.email || '—',
            d.phone || '—',
            d.vehicleNumber || '—',
            d.vehicleType || 'Car',
            d.isActive ? 'Active' : 'Inactive',
            formatDate(d.createdAt),
          ]),
          summary: { 'Total Drivers': drivers.length, 'Active': drivers.filter(d => d.isActive).length, 'Inactive': drivers.filter(d => !d.isActive).length },
        };

      case 'customer_list':
        return {
          title: 'Customer Directory',
          headers: ['Name', 'Email', 'Phone', 'Home Address', 'Status', 'Registered', 'Total Rides'],
          rows: customers.map(c => [
            c.name || '—',
            c.email || '—',
            c.phone || '—',
            c.homeAddress || '—',
            c.status || 'pending',
            formatDate(c.createdAt),
            rides.filter(r => r.customerId === c.id).length,
          ]),
          summary: { 'Total Customers': customers.length, 'Approved': customers.filter(c => c.status === 'approved').length, 'Pending': customers.filter(c => !c.status || c.status === 'pending').length },
        };

      default: return null;
    }
  };

  const downloadPDF = async (reportId) => {
    setGenerating(reportId + '_pdf');
    try {
      const data = getReportData(reportId);
      if (!data) return;

      const script1 = document.createElement('script');
      script1.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      document.head.appendChild(script1);
      await new Promise(r => script1.onload = r);

      const script2 = document.createElement('script');
      script2.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.5.31/jspdf.plugin.autotable.min.js';
      document.head.appendChild(script2);
      await new Promise(r => script2.onload = r);

      const { jsPDF } = window.jspdf;
      const doc = new jsPDF({ orientation: data.rows.length > 0 && data.headers.length > 6 ? 'landscape' : 'portrait', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();

      // Header bar
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, pageW, 22, 'F');
      doc.setTextColor(245, 158, 11);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('AK DISPATCH LLC', 14, 14);
      doc.setTextColor(148, 163, 184);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated: ${new Date().toLocaleString('en-US')}`, pageW - 14, 14, { align: 'right' });

      // Title
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(data.title, 14, 32);

      // Summary
      let summaryX = 14;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      Object.entries(data.summary).forEach(([key, val]) => {
        doc.text(`${key}: ${val}`, summaryX, 39);
        summaryX += 50;
      });

      // Table
      if (data.rows.length === 0) {
        doc.setTextColor(148, 163, 184);
        doc.setFontSize(12);
        doc.text('No data found for selected filters.', 14, 55);
      } else {
        doc.autoTable({
          startY: 44,
          head: [data.headers],
          body: data.rows,
          theme: 'grid',
          headStyles: { fillColor: [15, 23, 42], textColor: [245, 158, 11], fontStyle: 'bold', fontSize: 8 },
          bodyStyles: { fontSize: 8, textColor: [30, 41, 59] },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          margin: { left: 14, right: 14 },
        });
      }

      // Footer
      const pages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pages; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(148, 163, 184);
        const h = doc.internal.pageSize.getHeight();
        doc.text('AK Dispatch LLC — Confidential', 14, h - 6);
        doc.text(`Page ${i} of ${pages}`, pageW - 14, h - 6, { align: 'right' });
      }

      doc.save(`${data.title}.pdf`);
    } catch(err) {
      alert('PDF Error: ' + err.message);
    } finally {
      setGenerating('');
    }
  };

  const downloadExcel = async (reportId) => {
    setGenerating(reportId + '_excel');
    try {
      const data = getReportData(reportId);
      if (!data) return;

      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
      document.head.appendChild(script);
      await new Promise(r => script.onload = r);

      const XLSX = window.XLSX;
      const wsData = [data.headers, ...data.rows];
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Column widths
      ws['!cols'] = data.headers.map(() => ({ wch: 22 }));

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Report');

      // Summary sheet
      const summaryData = [['Metric', 'Value'], ...Object.entries(data.summary)];
      const ws2 = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, ws2, 'Summary');

      XLSX.writeFile(wb, `${data.title}.xlsx`);
    } catch(err) {
      alert('Excel Error: ' + err.message);
    } finally {
      setGenerating('');
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>Loading reports data...</div>;

  const dateRangeLabel = dateRange === 'today' ? 'Today'
    : dateRange === 'week' ? 'Last 7 Days'
    : dateRange === 'month' ? new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' })
    : dateRange === 'year' ? new Date().getFullYear().toString()
    : 'Custom Range';

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>
          Generate and download reports in PDF or Excel format
        </div>
      </div>

      {/* Global Filters */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20, marginBottom: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>🔍 Global Filters</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {/* Date Range */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 6 }}>Date Range</label>
            <select value={dateRange} onChange={e => setDateRange(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none', minWidth: 160 }}>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {dateRange === 'custom' && (
            <>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 6 }}>From</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                  style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 6 }}>To</label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                  style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none' }} />
              </div>
            </>
          )}

          {/* Daily specific date */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 6 }}>Specific Date (for Daily Report)</label>
            <input type="date" value={specificDate} onChange={e => setSpecificDate(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none' }} />
          </div>

          {/* Driver filter */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 6 }}>Filter by Driver</label>
            <select value={selectedDriver} onChange={e => setSelectedDriver(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none', minWidth: 160 }}>
              <option value="all">All Drivers</option>
              {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>

          {/* Customer filter */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 6 }}>Filter by Customer</label>
            <select value={selectedCustomer} onChange={e => setSelectedCustomer(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, outline: 'none', minWidth: 160 }}>
              <option value="all">All Customers</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Report Cards Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
        {REPORT_TYPES.map(report => {
          const data = getReportData(report.id);
          const count = data?.rows?.length || 0;
          const isGeneratingPDF   = generating === report.id + '_pdf';
          const isGeneratingExcel = generating === report.id + '_excel';
          const isGenerating = isGeneratingPDF || isGeneratingExcel;

          return (
            <div key={report.id} style={{
              background: '#fff', borderRadius: 12,
              border: activeReport === report.id ? '2px solid #f59e0b' : '1px solid #e2e8f0',
              padding: 20, transition: 'all 0.15s'
            }}>
              {/* Report Header */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 10, background: '#fef3c7',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0
                }}>
                  {report.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>{report.label}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{report.desc}</div>
                </div>
              </div>

              {/* Count badge */}
              <div style={{ marginBottom: 14 }}>
                <span style={{
                  background: count > 0 ? '#dbeafe' : '#f1f5f9',
                  color: count > 0 ? '#1e40af' : '#94a3b8',
                  padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600
                }}>
                  {count} {count === 1 ? 'record' : 'records'} · {dateRangeLabel}
                </span>
              </div>

              {/* Download Buttons */}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => downloadPDF(report.id)}
                  disabled={isGenerating || count === 0}
                  style={{
                    flex: 1, padding: '9px 12px', background: count > 0 ? '#ef4444' : '#f1f5f9',
                    border: 'none', borderRadius: 8, color: count > 0 ? '#fff' : '#94a3b8',
                    fontWeight: 600, fontSize: 13, cursor: count > 0 && !isGenerating ? 'pointer' : 'not-allowed',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    opacity: isGenerating ? 0.7 : 1
                  }}
                >
                  {isGeneratingPDF ? '⏳' : '📄'} {isGeneratingPDF ? 'Generating...' : 'PDF'}
                </button>
                <button
                  onClick={() => downloadExcel(report.id)}
                  disabled={isGenerating || count === 0}
                  style={{
                    flex: 1, padding: '9px 12px', background: count > 0 ? '#22c55e' : '#f1f5f9',
                    border: 'none', borderRadius: 8, color: count > 0 ? '#fff' : '#94a3b8',
                    fontWeight: 600, fontSize: 13, cursor: count > 0 && !isGenerating ? 'pointer' : 'not-allowed',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    opacity: isGenerating ? 0.7 : 1
                  }}
                >
                  {isGeneratingExcel ? '⏳' : '📊'} {isGeneratingExcel ? 'Generating...' : 'Excel'}
                </button>
              </div>

              {count === 0 && (
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8, textAlign: 'center' }}>
                  No data for selected filters
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Quick Stats */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20, marginTop: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>📊 Quick Overview</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
          {[
            { label: 'Total Rides',    value: filterRidesByDate(rides).length,                                                      color: '#3b82f6', icon: '🚗' },
            { label: 'Completed',      value: filterRidesByDate(rides).filter(r => r.status === 'completed').length,                color: '#22c55e', icon: '✅' },
            { label: 'Pending',        value: filterRidesByDate(rides).filter(r => r.status === 'pending').length,                  color: '#f59e0b', icon: '⏳' },
            { label: 'Cancelled',      value: filterRidesByDate(rides).filter(r => r.status === 'cancelled').length,                color: '#ef4444', icon: '❌' },
            { label: 'Active Drivers', value: drivers.filter(d => d.isActive).length,                                              color: '#8b5cf6', icon: '👤' },
            { label: 'Customers',      value: customers.filter(c => c.status === 'approved').length,                               color: '#06b6d4', icon: '🧑‍💼' },
          ].map(s => (
            <div key={s.label} style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 22 }}>{s.icon}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: s.color, marginTop: 4 }}>{s.value}</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
